import { AxiosResponse } from "axios";
import { KeyValue } from "../models/common";
import { api } from "./base";
import { IAnnouncementApi } from "./base/announcementApi";
import { Result } from "../models/common/resultResponse";
import {
	AnnouncementCompaniesRequest,
	AnnouncementFilterRequest,
	AnnouncementSaveRequest,
	AnnouncementCompaniesSaveRequest,
	CompanyAnnouncerFilterRequest,
	MarkAnnouncementReadRequest,
} from "../models/request/announcementRequest";
import { CompanyAnnouncementFilterRequest } from "../models/request";
import { PaginationResponse } from "../models/common/paginationResponse";
import {
	AnnouncementCompaniesResponse,
	AnnouncementDetailResponse,
	AnnouncementFileResponse,
	CompaniesAvailableResponse,
	IAnnouncement,
} from "../models/response/announcementResponse";
import { formatDateToISOString } from "../services/utilsDate";

const baseRoute: string = "Announcement";

export class AnnouncementApi extends IAnnouncementApi {
	getAnnouncements(request: AnnouncementFilterRequest): Promise<AxiosResponse<any>> {
		return api.get(`${baseRoute}`, {
			params: request,
		});
	}

	getStatusDomain(showExpired?: boolean): Promise<AxiosResponse<Result<KeyValue[]>>> {
		return api.get(`${baseRoute}/status-domain`, { params: { showExpired } });
	}

	save(request: AnnouncementSaveRequest, file: File | null): Promise<AxiosResponse<Result<string>>> {
		const formData = new FormData();
		Object.entries(request).forEach(([key, value]) => {
			if (value !== null && value !== undefined) {
				if (Array.isArray(value)) {
					value.forEach((item) => {
						formData.append(`${key}[]`, item.toString());
					});
					return;
				}

				if (typeof value === "boolean") {
					formData.append(key, value ? "true" : "false");
					return;
				}

				if (typeof value === "number") {
					formData.append(key, value.toString());
					return;
				}

				if (value instanceof Date) {
					formData.append(key, formatDateToISOString(value));
					return;
				}

				formData.append(key, value);
			}
		});

		if (file) {
			formData.append("formFile", file);
		}

		return api.post(`${baseRoute}/save`, formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
	}

	getDomainAnnouncements(guidCompany?: string): Promise<AxiosResponse<Result<KeyValue[]>>> {
		return api.get(`${baseRoute}/domain-announcements`, {
			params: { guidCompany },
		});
	}

	getAvailableCompaniesAnnouncement(
		request: CompanyAnnouncementFilterRequest,
	): Promise<AxiosResponse<PaginationResponse<CompaniesAvailableResponse>>> {
		return api.get(`${baseRoute}/available-companies`, {
			params: request,
		});
	}

	getDomainCompanyAnnouncers(request: CompanyAnnouncerFilterRequest): Promise<AxiosResponse<Result<KeyValue[]>>> {
		return api.get(`${baseRoute}/domain-announcer`, { params: request });
	}

	saveCompanies(request: AnnouncementCompaniesSaveRequest): Promise<AxiosResponse<Result<boolean>>> {
		return api.post(`${baseRoute}/save-companies`, request);
	}

	getDomainWhoCanSee(): Promise<AxiosResponse<Result<KeyValue[]>>> {
		return api.get(`${baseRoute}/domain-who-can-see`);
	}

	getDetailCompaniesWillSee(
		request: AnnouncementCompaniesRequest,
	): Promise<AxiosResponse<PaginationResponse<AnnouncementCompaniesResponse>>> {
		return api.get(`${baseRoute}/detail-companies`, {
			params: request,
		});
	}

	getDetail(guidAnnouncement: string): Promise<AxiosResponse<Result<AnnouncementDetailResponse>>> {
		return api.get(`${baseRoute}/detail`, {
			params: { guidAnnouncement },
		});
	}

	getFile(guidAnnouncement: string): Promise<AxiosResponse<Result<AnnouncementFileResponse>>> {
		return api.get(`${baseRoute}/file`, {
			params: { guidAnnouncement },
		});
	}

	copyAnnouncement(guidAnnouncement: string): Promise<AxiosResponse<Result<string>>> {
		return api.post(`${baseRoute}/copy`, null, {
			params: { guidAnnouncement },
		});
	}

	getAnnouncementsByUser(guidCompany: string): Promise<AxiosResponse<Result<IAnnouncement[]>>> {
		return api.get(`${baseRoute}/getAnnouncementsByUser`, {
			params: { guidCompany },
		});
	}

	markAsRead(request: MarkAnnouncementReadRequest): Promise<boolean> {
		return api.post(`${baseRoute}/readAnnouncement`, request);
	}
}
